import {
  Download,
  FileText,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  ACCEPT_ATTRIBUTE,
  isAcceptedFile,
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
  usePasteFiles,
} from '@/hooks/useAttachments';
import { useToast } from '@/components/ui/toast';
import { attachments as attachmentsApi } from '@/lib/api/endpoints';
import { pushOverlay } from '@/lib/overlayStack';
import { API_PREFIX } from '@/lib/api/client';
import type { Attachment } from '@/lib/api/types';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

/** Cross-origin images need credentials, or the API will 401 the request. */
const CROSS_ORIGIN = API_PREFIX.startsWith('http');

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A task's attachments.
 *
 * Three ways in, because the whole point is that adding a screenshot costs
 * nothing: paste (`⌘V`), drag onto the task, or the file picker. Paste is the
 * one the spec is built around — `⌘⇧4`, then `⌘V`, then type the title.
 */
export function Attachments({
  taskId,
  pasteEnabled = true,
}: {
  taskId: string;
  /** Off while another overlay owns the clipboard. */
  pasteEnabled?: boolean;
}) {
  const { data: files } = useAttachments(taskId);
  const upload = useUploadAttachment(taskId);
  const remove = useDeleteAttachment(taskId);
  const t = useT();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<Attachment | null>(null);

  const uploadAll = useCallback(
    (incoming: File[]) => {
      for (const file of incoming) {
        if (!isAcceptedFile(file)) {
          toast.error(t('attachment.unsupportedType', { name: file.name }));
          continue;
        }
        upload.mutate(file);
      }
    },
    [upload, toast, t],
  );

  usePasteFiles(uploadAll, pasteEnabled);

  const items = files ?? [];

  return (
    <section
      className="border-t border-border pt-4"
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        // Only clear when the pointer truly left, not when it crossed a child.
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setDragging(false);
        }
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        uploadAll([...event.dataTransfer.files]);
      }}
    >
      <div className="mb-2.5 flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
          {t('attachment.plural')}
        </h3>
        {items.length > 0 ? (
          <span className="text-[11px] tabular-nums text-content-subtle">
            {items.length}
          </span>
        ) : null}
        {upload.isPending ? (
          <Loader2 className="h-3 w-3 animate-spin text-content-subtle" aria-hidden />
        ) : null}
      </div>

      {items.length > 0 ? (
        <ul className="mb-2 grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-2">
          {items.map((attachment) => (
            <li key={attachment.id}>
              <AttachmentCard
                taskId={taskId}
                attachment={attachment}
                onOpen={() => setPreview(attachment)}
                onDelete={() => remove.mutate(attachment.id)}
              />
            </li>
          ))}
        </ul>
      ) : null}

      <div
        className={cn(
          'rounded-md border border-dashed px-3 py-2.5 text-sm transition-surface',
          dragging
            ? 'border-accent bg-accent-subtle text-accent'
            : 'border-border text-content-subtle',
        )}
      >
        {dragging ? (
          <span className="flex items-center gap-2">
            <Upload className="h-3.5 w-3.5" aria-hidden />
            Pusťte soubor sem
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <Paperclip className="h-3.5 w-3.5" aria-hidden />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="underline underline-offset-2 hover:text-content"
            >
              Vyberte soubor
            </button>
            <span>{t('attachment.dropHint')}</span>
            <kbd className="rounded border border-border px-1 text-[10px]">⌘V</kbd>
          </span>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTRIBUTE}
          className="sr-only"
          onChange={(event) => {
            uploadAll([...(event.target.files ?? [])]);
            // Reset, so choosing the same file twice fires onChange again.
            event.target.value = '';
          }}
        />
      </div>

      {preview ? (
        <Lightbox
          taskId={taskId}
          attachment={preview}
          onClose={() => setPreview(null)}
        />
      ) : null}
    </section>
  );
}

function AttachmentCard({
  taskId,
  attachment,
  onOpen,
  onDelete,
}: {
  taskId: string;
  attachment: Attachment;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const t = useT();
  const url = attachmentsApi.contentUrl(taskId, attachment.id);
  const isImage = attachment.mime_type.startsWith('image/');

  return (
    <div className="group relative overflow-hidden rounded-md border border-border bg-surface">
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left"
        aria-label={t('attachment.open', { name: attachment.original_filename })}
      >
        {isImage ? (
          <img
            src={url}
            alt={attachment.original_filename}
            loading="lazy"
            {...(CROSS_ORIGIN ? { crossOrigin: 'use-credentials' as const } : {})}
            // The stored dimensions reserve the space before the bytes arrive,
            // so a list of thumbnails does not jump as it loads.
            width={attachment.width ?? undefined}
            height={attachment.height ?? undefined}
            className="h-20 w-full bg-surface-sunken object-cover"
          />
        ) : (
          <span className="flex h-20 w-full items-center justify-center bg-surface-sunken">
            <FileText className="h-6 w-6 text-content-subtle" aria-hidden />
          </span>
        )}
        <span className="block truncate px-2 py-1.5 text-xs text-content">
          {attachment.original_filename}
        </span>
        <span className="block px-2 pb-1.5 text-[10px] text-content-subtle">
          {humanSize(attachment.file_size)}
        </span>
      </button>

      <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-surface group-hover:opacity-100 group-focus-within:opacity-100">
        <a
          href={url}
          download={attachment.original_filename}
          onClick={(event) => event.stopPropagation()}
          aria-label={t('attachment.downloadNamed', {
            name: attachment.original_filename,
          })}
          className="rounded bg-surface-raised/90 p-1 text-content-muted hover:text-content"
        >
          <Download className="h-3 w-3" aria-hidden />
        </a>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          aria-label={t('attachment.deleteNamed', {
            name: attachment.original_filename,
          })}
          className="rounded bg-surface-raised/90 p-1 text-content-muted hover:text-danger"
        >
          <Trash2 className="h-3 w-3" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/** Full-size preview. Escape or a click outside closes it. */
function Lightbox({
  taskId,
  attachment,
  onClose,
}: {
  taskId: string;
  attachment: Attachment;
  onClose: () => void;
}) {
  const t = useT();
  const url = attachmentsApi.contentUrl(taskId, attachment.id);
  const isImage = attachment.mime_type.startsWith('image/');

  useEffect(() => {
    // See lib/overlayStack: the dialog underneath declines Escape while this
    // is open, because capture-phase ordering means stopPropagation alone
    // cannot beat a listener that was registered first.
    const release = pushOverlay();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, { capture: true });

    return () => {
      release();
      document.removeEventListener('keydown', onKeyDown, { capture: true });
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/80 p-6"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-full max-w-5xl flex-col gap-2"
        onClick={(event) => event.stopPropagation()}
        role="presentation"
      >
        <div className="flex items-center gap-3 text-sm text-white">
          <span className="truncate">{attachment.original_filename}</span>
          <span className="shrink-0 text-white/60">
            {humanSize(attachment.file_size)}
          </span>
          <div className="flex-1" />
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/10"
          >
            <a href={url} download={attachment.original_filename}>
              <Download className="h-3.5 w-3.5" aria-hidden />
              {t('attachment.download')}
            </a>
          </Button>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('attachment.closePreview')}
            className="rounded p-1 text-white/80 hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {isImage ? (
          <img
            src={url}
            alt={attachment.original_filename}
            {...(CROSS_ORIGIN ? { crossOrigin: 'use-credentials' as const } : {})}
            className="max-h-[80vh] max-w-full rounded object-contain"
          />
        ) : (
          // PDFs render in the browser's own viewer — better than anything we
          // would build, and it already handles paging and search.
          <iframe
            src={url}
            title={attachment.original_filename}
            className="h-[80vh] w-[80vw] rounded bg-white"
          />
        )}
      </div>
    </div>
  );
}
